import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { Sparkles, Lock } from "lucide-react";
import { MarkdownText } from "../components/MarkdownText";
import { PremiumBenefits } from "../components/PremiumBenefits";

export default function Oroscopo() {
  const [data, setData] = useState(null);
  const [premium, setPremium] = useState(false);
  const [anon, setAnon] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/oroscopo-settimanale")
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) setAnon(true);
        else if (e?.response?.data?.detail?.code === "premium_required") setPremium(true);
        else setError(fmtErr(e));
      });
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16 sm:py-20" data-testid="oroscopo-page">
      <div className="text-center mb-10">
        <p className="eyebrow">Il tuo cielo, settimana dopo settimana</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-4">Oroscopo della settimana</h1>
        <p className="text-purple-200/70 mt-5 text-sm max-w-xl mx-auto leading-relaxed">
          Una previsione davvero tua: ogni lunedì Arcana incrocia il tuo tema natale con i transiti
          della settimana e ti avvisa con una notifica quando la previsione è pronta.
        </p>
      </div>

      {anon ? (
        <div className="mystic-card p-10 text-center" data-testid="oroscopo-login-gate">
          <Sparkles className="w-9 h-9 text-amber-400/60 mx-auto mb-5" />
          <p className="text-purple-200/75 text-sm mb-7 leading-relaxed">
            L'oroscopo settimanale personalizzato è riservato agli abbonati di Arcana: accedi o crea
            un account per continuare.
          </p>
          <Link to="/accedi?redirect=/oroscopo" data-testid="oroscopo-login-btn" className="gold-btn inline-block px-8 py-3 text-sm">
            Accedi o registrati
          </Link>
        </div>
      ) : premium ? (
        <div className="mystic-card p-10 text-center" data-testid="oroscopo-premium-gate">
          <Lock className="w-9 h-9 text-amber-400/60 mx-auto mb-5" />
          <p className="text-purple-200/75 text-sm mb-8 leading-relaxed">
            L'oroscopo settimanale personalizzato è riservato agli abbonati di Arcana.
          </p>
          <PremiumBenefits />
          <Link to="/piani" data-testid="oroscopo-premium-cta" className="gold-btn inline-block px-8 py-3 text-sm">
            Scopri l'abbonamento
          </Link>
        </div>
      ) : error ? (
        <div className="mystic-card p-8 text-center" data-testid="oroscopo-error">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      ) : !data ? (
        <p className="text-center text-purple-300/50 text-sm" data-testid="oroscopo-loading">Caricamento…</p>
      ) : data.text ? (
        <div className="mystic-card p-7 sm:p-9" data-testid="oroscopo-reading">
          <div className="flex items-center justify-center gap-2 mb-5">
            <Sparkles className="w-4 h-4 text-amber-300/70" />
            <p className="eyebrow" data-testid="oroscopo-week-label">Settimana {data.week_label || data.week}</p>
          </div>
          {data.partial && (
            <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-center" data-testid="oroscopo-partial-banner">
              <p className="text-purple-200/75 text-xs leading-relaxed">
                Questa previsione usa la tua data di nascita e i transiti reali della settimana. Aggiungendo{" "}
                <Link to="/cielo" className="text-amber-300 hover:underline">ora e luogo di nascita</Link>{" "}
                diventa ancora più puntuale: si aggiungono ascendente, discendente e case astrologiche.
              </p>
            </div>
          )}
          <div className="text-purple-100/85 leading-loose text-sm sm:text-base whitespace-pre-line" data-testid="oroscopo-text">
            <MarkdownText text={data.text} />
          </div>
        </div>
      ) : (
        <div className="mystic-card p-10 text-center" data-testid="oroscopo-pending">
          <Sparkles className="w-9 h-9 text-amber-400/60 mx-auto mb-5" />
          <p className="text-purple-200/75 text-sm leading-relaxed mb-2">
            La previsione di questa settimana è in preparazione: la riceverai lunedì mattina con una notifica.
          </p>
          {data.previous?.text && (
            <p className="text-purple-300/50 text-xs mt-4">
              Nel frattempo puoi rileggere quella della settimana scorsa ({data.previous.week_label || data.previous.week}) qui sotto.
            </p>
          )}
          {data.previous?.text && (
            <div className="mt-6 pt-6 border-t border-amber-500/15 text-left text-purple-100/75 leading-loose text-sm whitespace-pre-line" data-testid="oroscopo-previous">
              <MarkdownText text={data.previous.text} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
